/**
 * Regras de tipo de OS x flags LCMY_REG_BUGS (abertura de OS).
 * Alinhado aos tipos da tela Defeitos (defeitosVerveTableUi.TIPO_OS_FIELDS).
 */

const TIPO_TO_FLAG = {
  N: 'allow_corrective',
  I: 'allow_install',
  S: 'allow_preventive',
  D: 'allow_desinstall',
  R: 'allow_recharge',
  E: 'allow_stock',
  A: 'allow_measurement',
  B: 'allow_counter',
};

/** Tipos legados da abertura (sem flag de restrição ativa). */
const LEGACY_TIPOS = ['N', 'S', 'A', 'D', 'R', 'B'];

/** Flags de LCY_SYS_USERS_LOAD → valor de tipo na abertura. */
export const USER_LOAD_OS_FLAG_TO_VALUE = {
  os_normal: 'N',
  os_install: 'I',
  os_preventive: 'S',
  os_reading: 'A',
  os_uninstall: 'D',
  os_return: 'R',
  os_stock: 'E',
  os_counter: 'B',
};

/** Converte registro LCY_SYS_USERS_LOAD em valores N/I/S/… da abertura. */
export function userOsLoadFlagsToValues(osTypes) {
  if (!osTypes || typeof osTypes !== 'object') return [];
  const out = [];
  Object.keys(USER_LOAD_OS_FLAG_TO_VALUE).forEach((key) => {
    const v = osTypes[key];
    if (v === true || v === 1 || v === '1') out.push(USER_LOAD_OS_FLAG_TO_VALUE[key]);
  });
  return out;
}

const FLAG_READ_KEYS = {
  allow_corrective: ['allow_corrective', 'allowCorrective'],
  allow_install: ['allow_install', 'allowInstall'],
  allow_desinstall: ['allow_desinstall', 'allowDesinstall'],
  allow_preventive: ['allow_preventive', 'allowPreventive'],
  allow_stock: ['allow_stock', 'allowStock'],
  allow_measurement: ['allow_measurement', 'allowMeasurement'],
  allow_recharge: ['allow_recharge', 'allowRecharge'],
  allow_counter: ['allow_counter', 'allowCounter'],
};

/** Tipos configuráveis na tela de Defeitos (iniciais para badge). */
export const DEFEITO_TIPO_OS_BADGE_FIELDS = [
  { db: 'allow_corrective', sigla: 'C', label: 'Corretiva', value: 'N' },
  { db: 'allow_install', sigla: 'I', label: 'Instalação', value: 'I' },
  { db: 'allow_desinstall', sigla: 'D', label: 'Desinstalação', value: 'D' },
  { db: 'allow_preventive', sigla: 'P', label: 'Preventiva', value: 'S' },
  { db: 'allow_stock', sigla: 'E', label: 'Estoque', value: 'E' },
  { db: 'allow_recharge', sigla: 'R', label: 'Recarga', value: 'R' },
  { db: 'allow_measurement', sigla: 'A', label: 'Aferição', value: 'A' },
  { db: 'allow_counter', sigla: 'B', label: 'Balcão', value: 'B' },
];

export const CONFIG_TIPO_OS_OPTIONS = DEFEITO_TIPO_OS_BADGE_FIELDS.map((f) => ({
  value: f.value,
  label: f.label,
  db: f.db,
}));

export const TIPO_OS_VALUE_LABELS = {
  N: 'Corretiva',
  I: 'Instalação',
  S: 'Preventiva',
  A: 'Aferição',
  D: 'Desinstalação',
  R: 'Recarga',
  E: 'Estoque',
  B: 'Balcão',
};

function readDefeitoField(defeito, db) {
  if (!defeito) return undefined;
  const keys = FLAG_READ_KEYS[db] || [db];
  for (let i = 0; i < keys.length; i += 1) {
    const v = defeito[keys[i]];
    if (v !== undefined && v !== null) return v;
  }
  return undefined;
}

function readHasRegBug(defeito) {
  if (!defeito) return undefined;
  if (defeito.has_reg_bug !== undefined && defeito.has_reg_bug !== null) return defeito.has_reg_bug;
  if (defeito.hasRegBug !== undefined && defeito.hasRegBug !== null) return defeito.hasRegBug;
  return undefined;
}

/** 0 = desligado; 1 = ligado; null/undefined = padrão ligado (ISNULL no backend). */
export function normOsTipoFlag(v) {
  if (v === 0 || v === false || v === '0') return 0;
  if (v === undefined || v === null) return 1;
  return 1;
}

function countTipoOsOn(defeito) {
  return DEFEITO_TIPO_OS_BADGE_FIELDS.reduce(
    (acc, f) => acc + normOsTipoFlag(readDefeitoField(defeito, f.db)),
    0
  );
}

export function isTipoAllowedForDefeito(defeito, tipoValue) {
  if (!defeito || !tipoValue) return false;
  const col = TIPO_TO_FLAG[tipoValue];
  if (!col) return true;
  return normOsTipoFlag(readDefeitoField(defeito, col)) === 1;
}

/** Valores (N/I/S/D/E/R) ligados no cadastro do defeito. */
export function getConfigTipoValuesForDefeito(defeito) {
  if (!defeito) return [];
  return CONFIG_TIPO_OS_OPTIONS
    .filter((o) => normOsTipoFlag(readDefeitoField(defeito, o.db)) === 1)
    .map((o) => o.value);
}

/**
 * Tipos exibíveis na abertura quando a restrição por defeito está ativa.
 * @param {object} defeito
 * @param {{ restrictionActive?: boolean, origem?: 'contrato'|'estoque'|null }} [opts]
 */
export function getAllowedTipoValuesForAbertura(defeito, opts = {}) {
  const { restrictionActive = false, origem = null } = opts;
  if (!restrictionActive) return [...LEGACY_TIPOS];
  if (!defeito) return [];
  let values = getConfigTipoValuesForDefeito(defeito);
  if (origem === 'contrato') values = values.filter((v) => v !== 'E');
  return values;
}

/** Defeito pode ser usado na abertura para a origem informada (flag de restrição ativa). */
export function isDefeitoAllowedForAbertura(defeito, origem, restrictionActive) {
  if (!restrictionActive || !defeito) return true;
  const allowed = getConfigTipoValuesForDefeito(defeito);
  if (!allowed.length) return false;
  if (origem === 'estoque') return allowed.includes('E');
  if (origem === 'contrato') return allowed.some((v) => v !== 'E');
  return true;
}

export function getAllowedTipoValues(defeito) {
  if (!defeito) return [...LEGACY_TIPOS];
  return LEGACY_TIPOS.filter((t) => isTipoAllowedForDefeito(defeito, t));
}

/** Há registro em LCMY_REG_BUGS e nem todos os 6 tipos estão ligados. */
export function hasTipoOsRestriction(defeito) {
  if (!defeito) return false;
  const reg = readHasRegBug(defeito);
  if (reg === 0 || reg === false || reg === '0') return false;
  const nOn = countTipoOsOn(defeito);
  return nOn > 0 && nOn < DEFEITO_TIPO_OS_BADGE_FIELDS.length;
}

export function getDefeitoTipoBadges(defeito, opts = {}) {
  if (!hasTipoOsRestriction(defeito)) return [];
  const allowedValues = Array.isArray(opts.allowedValues) ? opts.allowedValues : null;
  return DEFEITO_TIPO_OS_BADGE_FIELDS.filter((f) => {
    if (normOsTipoFlag(readDefeitoField(defeito, f.db)) !== 1) return false;
    if (allowedValues && allowedValues.indexOf(f.value) < 0) return false;
    return true;
  }).map((f) => ({
    sigla: f.sigla,
    label: f.label,
  }));
}

/** Tipos operacionais quando o defeito restringe (legado — preferir getAllowedTipoValuesForAbertura). */
export function getOperationalAllowedTipoValues(defeito) {
  if (!hasTipoOsRestriction(defeito)) return [...LEGACY_TIPOS];
  const mapped = getConfigTipoValuesForDefeito(defeito);
  return mapped.length ? mapped : [...LEGACY_TIPOS];
}
